/**
 * App-wide cache for data that rarely changes. Every cached item lives in one of the buckets below.
 *
 * Layers: memory first (fast), sessionStorage behind it (survives a page refresh). sessionStorage is
 * per browser tab and is wiped when the tab closes; clearAllCaches() also wipes it at login and logout,
 * so one user's data never leaks to the next.
 *
 * appCache knows no API URLs: services pass their own fetch function to getOrFetch(), and are
 * responsible for invalidating their bucket after their own create/update/delete calls.
 */

const BUCKETS = {
  // Academic years with status and dates. Key: "all". Refreshed after creating, initializing,
  // activating, syncing or closing a year.
  academicYears: "academicYears",
  // Grades, each with its active sections. Key: "all". Refreshed after grade or section changes.
  grades: "grades",
  // All sections. Key: "all". Refreshed after a section is created.
  sections: "sections",
  // All subjects. Key: "all". Refreshed after a subject is created, updated or deleted.
  subjects: "subjects",
  // Term fee per grade for one academic year. Key: academic year name, e.g. "2025-26".
  // Also read by student import validation. Refreshed after saving fees for that year.
  feeStructures: "feeStructures",
  // Dashboard data as { fetchedAt, data }. Key: "admin" or "student". Refreshed by the dashboard's
  // refresh button and after a fee payment.
  dashboard: "dashboard",
  // Settings → School Profile (admin only), or null before one is saved. Key: "all". Refreshed after
  // saving the profile or changing the logo.
  schoolProfile: "schoolProfile",
  // The school logo as a data URL, or null when there is none. Key: "all". Refreshed after the logo changes.
  schoolLogo: "schoolLogo",
} as const;

export type CacheBucket = keyof typeof BUCKETS;

const ALL = "all";
const STORAGE_PREFIX = "msp-cache:";
// Dashboard data used to live in localStorage under this prefix; cleared once so it doesn't linger
const LEGACY_DASHBOARD_PREFIX = "dashboard-cache-";

const memory = new Map<string, unknown>();
// Fetches in progress, so two pages asking at once share one request
const inFlight = new Map<string, Promise<unknown>>();

const storageKey = (bucket: CacheBucket, key: string) => `${STORAGE_PREFIX}${BUCKETS[bucket]}:${key}`;

const get = <T>(bucket: CacheBucket, key: string = ALL): T | undefined => {
  const k = storageKey(bucket, key);
  if (memory.has(k)) return memory.get(k) as T;
  try {
    const raw = sessionStorage.getItem(k);
    if (raw === null) return undefined;
    const value = JSON.parse(raw) as T;
    memory.set(k, value);
    return value;
  } catch {
    return undefined;
  }
};

const set = <T>(bucket: CacheBucket, value: T, key: string = ALL): void => {
  const k = storageKey(bucket, key);
  memory.set(k, value);
  try {
    sessionStorage.setItem(k, JSON.stringify(value));
  } catch {
    // storage full or blocked: the memory layer still works for this page session
  }
};

/** Returns the cached value, or calls `fetcher`, caches its result and returns it. Failures are not cached. */
const getOrFetch = async <T>(bucket: CacheBucket, fetcher: () => Promise<T>, key: string = ALL): Promise<T> => {
  const cached = get<T>(bucket, key);
  if (cached !== undefined) return cached;

  const k = storageKey(bucket, key);
  const pending = inFlight.get(k);
  if (pending) return pending as Promise<T>;

  const request = fetcher()
    .then((value) => {
      set(bucket, value, key);
      return value;
    })
    .finally(() => inFlight.delete(k));
  inFlight.set(k, request);
  return request;
};

/** Drops one key of a bucket, or the whole bucket when no key is given. */
const invalidate = (bucket: CacheBucket, key?: string): void => {
  const prefix = key === undefined ? `${STORAGE_PREFIX}${BUCKETS[bucket]}:` : storageKey(bucket, key);
  const matches = (k: string) => (key === undefined ? k.startsWith(prefix) : k === prefix);
  Array.from(memory.keys()).filter(matches).forEach((k) => memory.delete(k));
  try {
    Object.keys(sessionStorage).filter(matches).forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // storage blocked: nothing persisted to remove
  }
};

/** Wipes every bucket. Called at login and logout. */
export const clearAllCaches = (): void => {
  memory.clear();
  inFlight.clear();
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(STORAGE_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
    Object.keys(localStorage)
      .filter((k) => k.startsWith(LEGACY_DASHBOARD_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // storage blocked: memory is already cleared
  }
};

export const appCache = { get, set, getOrFetch, invalidate };
